import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SchoolIcon from '@mui/icons-material/School';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lessonService } from '../services/lessonService';
import { useAuthStore } from '../store/authStore';
import type { CEFRLevel, Lesson, LessonStatus } from '../types';

const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const STATUSES: LessonStatus[] = ['draft', 'in_review', 'approved', 'published'];

const STATUS_COLORS: Record<LessonStatus, 'default' | 'warning' | 'info' | 'success'> = {
  draft: 'default',
  in_review: 'warning',
  approved: 'info',
  published: 'success',
};

const CEFR_STRIPE_COLORS: Record<CEFRLevel, string> = {
  A1: 'success.light',
  A2: 'success.light',
  B1: 'primary.light',
  B2: 'primary.light',
  C1: 'secondary.light',
  C2: 'secondary.light',
};

export const BrowseLessons: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [cefrFilter, setCefrFilter] = useState<CEFRLevel | ''>('');
  const [statusFilter, setStatusFilter] = useState<LessonStatus | ''>('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['browse-lessons', { page, searchQuery, cefrFilter, statusFilter }],
    queryFn: () =>
      lessonService.getAll({
        page,
        per_page: 12,
        search: searchQuery || undefined,
        cefr_level: cefrFilter || undefined,
        status: statusFilter || undefined,
      }),
  });

  const lessons = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const perPage = data?.meta?.per_page ?? 12;
  const pageCount = Math.ceil(total / perPage);

  const duplicateMutation = useMutation({
    mutationFn: lessonService.duplicate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['browse-lessons'] }),
  });

  const handleDownload = (lesson: Lesson) => {
    const url = `${process.env.REACT_APP_API_URL || '/api'}/lessons/${lesson.id}/versions/${lesson.current_version}/export`;
    const token = localStorage.getItem('lessoncraft_token');
    window.open(`${url}?token=${token}`, '_blank');
  };

  const toggleCefr = (level: CEFRLevel) => {
    setCefrFilter((prev) => (prev === level ? '' : level));
    setPage(1);
  };

  const toggleStatus = (status: LessonStatus) => {
    setStatusFilter((prev) => (prev === status ? '' : status));
    setPage(1);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', fontFamily: '"Noto Sans", sans-serif' }}>
      {/* Hero search bar */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          p: 3,
          pb: 4,
        }}
      >
        <Typography variant="h5" fontWeight={700} color="white">
          Browse Lessons
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
          {user?.role === 'author'
            ? 'View published lessons and your own drafts'
            : 'Browse all lessons across the platform'}
        </Typography>
        <TextField
          fullWidth
          size="medium"
          placeholder="Search lessons…"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            mt: 2,
            bgcolor: 'white',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        />
      </Box>

      {/* Filters section */}
      <Box sx={{ p: 3, pt: 1.5 }}>
        {/* CEFR Level chip pills */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Level:
          </Typography>
          {CEFR_LEVELS.map((level) => (
            <Chip
              key={level}
              label={level}
              size="small"
              clickable
              color={cefrFilter === level ? 'primary' : 'default'}
              variant={cefrFilter === level ? 'filled' : 'outlined'}
              onClick={() => toggleCefr(level)}
            />
          ))}
        </Box>

        {/* Status chip pills */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Status:
          </Typography>
          {STATUSES.map((status) => (
            <Chip
              key={status}
              label={status.replace('_', ' ')}
              size="small"
              clickable
              color={statusFilter === status ? STATUS_COLORS[status] : 'default'}
              variant={statusFilter === status ? 'filled' : 'outlined'}
              onClick={() => toggleStatus(status)}
            />
          ))}
        </Box>

        {/* Result count */}
        {!isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {total} lesson{total !== 1 ? 's' : ''} found
          </Typography>
        )}
      </Box>

      {isError && (
        <Box sx={{ px: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load lessons.
          </Alert>
        </Box>
      )}

      {/* Lesson grid */}
      {isLoading ? (
        <Grid container spacing={2.5} sx={{ px: 3 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : lessons.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.disabled">
            No lessons found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Try adjusting your filters or search terms.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5} sx={{ px: 3 }}>
          {lessons.map((lesson) => (
            <Grid item key={lesson.id} xs={12} sm={6} md={4} lg={3}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease',
                  },
                }}
              >
                {/* Colored CEFR stripe */}
                <Box
                  sx={{
                    height: 4,
                    bgcolor: CEFR_STRIPE_COLORS[lesson.cefr_level] ?? 'primary.light',
                  }}
                />
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                    <Chip label={lesson.cefr_level} size="small" color="primary" />
                    <Chip
                      label={lesson.status.replace('_', ' ')}
                      size="small"
                      color={STATUS_COLORS[lesson.status]}
                    />
                  </Stack>
                  <Typography variant="h6" fontWeight={700} noWrap sx={{ mb: 0.5 }}>
                    {lesson.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
                    {lesson.topic}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    By {lesson.author?.name ?? lesson.author_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lesson.duration_min} min
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, gap: 0.5 }}>
                  <Tooltip title="Duplicate to my lessons">
                    <IconButton
                      size="small"
                      onClick={() => duplicateMutation.mutate(lesson.id)}
                      disabled={duplicateMutation.isPending}
                    >
                      {duplicateMutation.isPending ? (
                        <CircularProgress size={16} />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  {lesson.status === 'published' && (
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleDownload(lesson)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, pb: 4 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};
